import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ComboBox, Flex, Item, Text, View } from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { SettingItem } from "../../../../services/soap/features/responses/GetFormSettingsResponse";
import { IColumnItem } from "../../../../components/protected/Dialogs/components/ColumnsList";
import { IFilterControl, IFilterItem } from "../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/FilterSetupPlaceholder";
import { FilterControlRenderer } from "../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/components/FilterControlRenderer/FilterControlRenderer";
import { convertFilterControlToFilterParam, FilterParam } from "./filterUtils";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import { TrashIcon } from "../../../../components/protected/Icons/IconsLib";
import styles from "./QuickFilterControlsComponent.module.css";

interface IQuickFilterControlsComponentProps {
  formSettings?: SettingItem[];
  filterParams: any;
  onFilterChange: (filterParams: {}) => void;
  fpaTypeCategory?: string;
}

// Transform SettingItem[] to flat IColumnItem[] array
const transformFormSettingsToColumnItems = (settingItems: SettingItem[]): IColumnItem[] => {
  if (!settingItems || settingItems.length === 0) {
    return [];
  }

  return settingItems
    .filter((item) => {
      // Check if this is a filter-only column (coltype starts with 'filter.')
      const isFilterOnlyColumn = item.coltype?.startsWith('filter.') === true;
      // Only include visible columns (visibility === 1) and exclude filter-only columns
      return !isFilterOnlyColumn && item.visibility == 1;
    })
    .map((item) => ({
      id: String(item.id),
      label: item.caption && item.caption.trim() ? item.caption : item.name,
      itemname: item.itemname,
      type: item.coltype || 'text',
      isVisible: true,
      isFilter: false,
    }));
};

const QuickFilterControlsComponent: React.FC<IQuickFilterControlsComponentProps> = ({
  formSettings,
  filterParams,
  onFilterChange,
  fpaTypeCategory,
}) => {
  const { t } = useTranslation();
  const { store } = useDependency();
  const [quickFilters, setQuickFilters] = useState<IFilterControl[]>([]);
  const [comboBoxValue, setComboBoxValue] = useState<string | null>(null);
  const isUpdatingFromParams = useRef(false);
  const prevFilterParamsRef = useRef<any>(null);

  const {
    folderTypes: folderTypesState,
    projectTypes: projectTypesState,
    folderStatusMapping2: folderStatusState2,
    folderStatusMapping: folderStatusState,
    projectStatusMapping: projectStatusState,
    activityStatusMapping: activityStatusState,
    activityTypes: activityTypesState,
  } = usePreloadAssets();

  // Transform formSettings to IColumnItem[]
  const allFields = useMemo(() => {
    if (!formSettings) return [];
    return transformFormSettingsToColumnItems(formSettings);
  }, [formSettings]);

  // Get available fields (exclude already selected quick filters)
  const availableFields = useMemo(() => {
    const usedFieldIds = new Set(quickFilters.map(f => f.data.id));
    return allFields.filter(field => !usedFieldIds.has(field.id));
  }, [allFields, quickFilters]);

  // Map status records to options
  const mapStatusRecordToOptions = useCallback(
    (record: Record<string, any> | undefined): any[] => {
      if (!record) return [];
      return Object.entries(record).map(([id, value]) => ({
        id: String(id),
        label: value?.name ?? value?.status?.name ?? String(id),
        color: value?.status?.color ?? value?.color ?? value?.STATUS?.color ?? "#9E9E9E",
      }));
    },
    []
  );

  // Map type records to options
  const mapTypeRecordToOptions = useCallback(
    (record: Record<string, any> | any[] | undefined): any[] => {
      if (!record) return [];
      const values = Array.isArray(record) ? record : Object.values(record);

      return values
        .map((value) => {
          const rawId =
            value?.id ??
            value?.TYPE?.id ??
            value?.typeId ??
            value?.TYPE_ID ??
            value?.TypeId;
          const rawLabel =
            value?.name ??
            value?.TYPE?.name ??
            value?.typeName ??
            value?.TYPE_NAME ??
            value?.TypeName ??
            (rawId !== undefined ? String(rawId) : undefined);

          if (rawId === undefined && !rawLabel) {
            return null;
          }

          return {
            id: String(rawId ?? rawLabel),
            label: rawLabel ? String(rawLabel) : String(rawId),
          };
        })
        .filter((option): option is any => Boolean(option));
    },
    []
  );

  const folderStatusOptions = mapStatusRecordToOptions(
    folderStatusState2 || folderStatusState
  );
  const projectStatusOptions = mapStatusRecordToOptions(projectStatusState);
  const activityStatusOptions = mapStatusRecordToOptions(activityStatusState);

  const folderTypeOptions = mapTypeRecordToOptions(folderTypesState);
  const projectTypeOptions = mapTypeRecordToOptions(projectTypesState);
  const activityTypeOptions = mapTypeRecordToOptions(activityTypesState);

  // Handle field selection from ComboBox
  const handleFieldSelection = useCallback(
    (key: string | null) => {
      if (!key) {
        setComboBoxValue(null);
        return;
      }

      // Find the selected field from all fields
      const selectedField = allFields.find(field => field.id === key);
      if (!selectedField) {
        setComboBoxValue(null);
        return;
      }

      // Create a new filter control
      const newFilter: IFilterControl = {
        id: `quick-filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: selectedField.itemname,
        label: selectedField.label,
        dataType: selectedField.type || 'text',
        data: selectedField,
        filterInfo: [{ name: 'value', type: 'contains', value: '' }],
      };

      // Add the new filter to the list
      setQuickFilters(prev => [...prev, newFilter]);

      // Reset ComboBox selection
      setComboBoxValue(null);
    },
    [allFields]
  );

  // Handle filter deletion
  const handleDeleteFilter = useCallback((filterId: string) => {
    setQuickFilters(prev => {
      const updated = prev.filter(filter => filter.id !== filterId);
      
      // Also remove from filterParams
      const filterToRemove = prev.find(f => f.id === filterId);
      if (filterToRemove) {
        const filterParam = convertFilterControlToFilterParam(filterToRemove, store);
        if (filterParam) {
          const updatedFilterParams = { ...filterParams };
          if (!updatedFilterParams.FILTER) {
            updatedFilterParams.FILTER = { COL: [] };
          }
          updatedFilterParams.FILTER.COL = updatedFilterParams.FILTER.COL.filter(
            (col: FilterParam) => col.name !== filterParam.name
          );
          
          // Update prevFilterParamsRef to prevent sync loop
          prevFilterParamsRef.current = updatedFilterParams;
          isUpdatingFromParams.current = true;
          
          // Call onFilterChange to notify parent
          onFilterChange(updatedFilterParams);
          
          // Reset flag after state update
          setTimeout(() => {
            isUpdatingFromParams.current = false;
          }, 0);
        }
      }
      
      return updated;
    });
  }, [filterParams, onFilterChange, store]);

  // Handle filter value change
  const handleFilterChange = useCallback(
    (updatedFilter: IFilterControl) => {
      // Update the filter in quickFilters
      setQuickFilters(prev =>
        prev.map(filter => filter.id === updatedFilter.id ? updatedFilter : filter)
      );

      // Convert IFilterControl to FilterParam and update filterParams
      const filterParam = convertFilterControlToFilterParam(updatedFilter, store);

      if (!filterParam) {
        return;
      }

      // Update filterParams.FILTER.COL array
      const updatedFilterParams = { ...filterParams };
      if (!updatedFilterParams.FILTER) {
        updatedFilterParams.FILTER = { COL: [] };
      }
      if (!updatedFilterParams.FILTER.COL) {
        updatedFilterParams.FILTER.COL = [];
      }

      // Find existing entry with same name
      const existingIndex = updatedFilterParams.FILTER.COL.findIndex(
        (col: FilterParam) => col.name === filterParam.name
      );

      // Allow empty values for 'empty' type filters (is_empty/is_not_empty)
      const isEmptyType = filterParam.type === 'empty';
      const isEmptyValue = filterParam.value === '' || filterParam.value === null || filterParam.value === undefined;

      if (!isEmptyType && isEmptyValue) {
        // Remove entry if value is empty (but preserve 'empty' type filters)
        updatedFilterParams.FILTER.COL = updatedFilterParams.FILTER.COL.filter(
          (col: FilterParam) => col.name !== filterParam.name
        );
      } else if (existingIndex !== -1) {
        // Update existing entry
        updatedFilterParams.FILTER.COL[existingIndex] = filterParam;
      } else {
        // Add new entry
        updatedFilterParams.FILTER.COL.push(filterParam);
      }

      // Remove empty values (but preserve 'empty' type filters which have empty values)
      updatedFilterParams.FILTER.COL = updatedFilterParams.FILTER.COL.filter(
        (col: FilterParam) => col.type === 'empty' || (col.value !== '' && col.value !== null && col.value !== undefined)
      );

      // Update prevFilterParamsRef to prevent sync loop
      prevFilterParamsRef.current = updatedFilterParams;
      isUpdatingFromParams.current = true;

      // Call onFilterChange to notify parent
      onFilterChange(updatedFilterParams);

      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromParams.current = false;
      }, 0);
    },
    [filterParams, onFilterChange, store]
  );

  // Sync quickFilters from filterParams (bidirectional sync)
  useEffect(() => {
    // Skip if we're currently updating from filterParams to avoid loops
    if (isUpdatingFromParams.current) {
      return;
    }

    // Skip if filterParams hasn't actually changed
    if (prevFilterParamsRef.current === filterParams) {
      return;
    }

    // Get current filterParams.FILTER.COL
    const currentFilterCol = filterParams?.FILTER?.COL || [];
    const prevFilterCol = prevFilterParamsRef.current?.FILTER?.COL || [];

    // Compare filterParams.FILTER.COL arrays
    const hasChanged =
      JSON.stringify(currentFilterCol) !== JSON.stringify(prevFilterCol);

    if (!hasChanged) {
      prevFilterParamsRef.current = filterParams;
      return;
    }

    // Update quickFilters based on filterParams
    const updatedQuickFilters = quickFilters.map((control) => {
      const matchingFilterParam = currentFilterCol.find(
        (col: FilterParam) => col.name === control.name
      );

      if (matchingFilterParam) {
        // Update filterInfo from filterParam
        const filterItem: IFilterItem = {
          name: 'value',
          type: matchingFilterParam.type === 'empty' 
            ? (matchingFilterParam.negation ? 'is_not_empty' : 'is_empty')
            : matchingFilterParam.type === 'in' ? 'equals' : matchingFilterParam.type,
          value: matchingFilterParam.value || '',
          negation: matchingFilterParam.negation,
        };

        return {
          ...control,
          filterInfo: [filterItem],
        };
      } else {
        // If filterParam doesn't exist, clear the filterInfo
        return {
          ...control,
          filterInfo: [{ name: 'value', type: 'contains', value: '' }],
        };
      }
    });

    isUpdatingFromParams.current = true;
    setQuickFilters(updatedQuickFilters);
    prevFilterParamsRef.current = filterParams;
    
    // Reset flag after state update
    setTimeout(() => {
      isUpdatingFromParams.current = false;
    }, 0);
  }, [filterParams, quickFilters]);

  // Don't render if no formSettings
  if (!formSettings || formSettings.length === 0) {
    return null;
  }

  return (
    <Flex
      direction={"column"}
      width={"100%"}
      alignItems={"start"}
      justifyContent={"start"}
      gap={"12px"}
      UNSAFE_className={styles.quickFilterContainer}
    >
      <View width={"100%"} UNSAFE_className={styles.quickFilterHeader}>
        <Text UNSAFE_className={styles.quickFilterTitle}>
          {t("quick_filters", { ns: "layout_components", defaultValue: "Quick Filters" })}
        </Text>
      </View>

      {/* Render quick filters */}
      {quickFilters.length > 0 && (
        <Flex
          direction={"column"}
          width={"100%"}
          alignItems={"start"}
          justifyContent={"start"}
          gap={"12px"}
          UNSAFE_className={styles.quickFiltersList}
        >
          {quickFilters.map((filter) => (
            <View key={filter.id} width={"100%"} UNSAFE_className={styles.quickFilterItem}>
              <Flex
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
                UNSAFE_className={styles.quickFilterHeader}
              >
                <Text UNSAFE_className={styles.quickFilterName}>{filter.label || filter.name}</Text>
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => handleDeleteFilter(filter.id)}
                  aria-label={`Delete ${filter.label || filter.name} filter`}
                >
                  <TrashIcon size={20} />
                </button>
              </Flex>
              <View width={"100%"} UNSAFE_className={styles.quickFilterContent}>
                <FilterControlRenderer
                  filter={filter}
                  onFilterChange={handleFilterChange}
                  statusOptions={{
                    folder: folderStatusOptions,
                    project: projectStatusOptions,
                    activity: activityStatusOptions,
                  }}
                  typeOptions={{
                    folder: folderTypeOptions,
                    project: projectTypeOptions,
                    activity: activityTypeOptions,
                  }}
                  fpaTypeCategory={fpaTypeCategory}
                />
              </View>
            </View>
          ))}
        </Flex>
      )}

      {/* Add Filter ComboBox */}
      {availableFields.length > 0 && (
        <View width={"100%"} UNSAFE_className={styles.quickFilterComboBoxWrapper}>
          <ComboBox
            aria-label={t("add_filter", { ns: "layout_components", defaultValue: "Add Filter" })}
            width="100%"
            selectedKey={comboBoxValue}
            onSelectionChange={(key) => handleFieldSelection(key as string | null)}
            description={t("add_filter", { ns: "layout_components", defaultValue: "Add Filter" })}
          >
            {availableFields.map((field) => (
              <Item key={field.id} textValue={field.label}>
                {field.label}
              </Item>
            ))}
          </ComboBox>
        </View>
      )}
    </Flex>
  );
};

export default QuickFilterControlsComponent;
